import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  activeSessionDay,
  getActiveSession,
  getStoreActivity,
  logoutSession,
  startDay,
  tableLayoutAvailability,
} from "../../../../../../config/Services";
import { setInLocalStorage } from "../../../../../../utils/localStorageHelpers";
import {
  addLicense,
  addadvance,
  addmanagerpin,
  addmanageruidx,
  addpin,
} from "../../../all-products/redux/Actions/SessionActions";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import useGetDevice from "../../../hooks/useGetDevice";
import { customEventTrigger } from "../../../../../../analytics";
import PalletLogo from "../../../../../../assets/images/Pallet-logo-blue-transparent (2).png";
import ComputerIcon from "@mui/icons-material/Computer";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LogoutIcon from "@mui/icons-material/Logout";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import { getMobilePos } from "../../../all-products/redux/Reducers/paginationReducer";
import { emit } from "react-native-react-bridge/lib/web";
import CircularProgress from "@mui/material/CircularProgress";
import "./indexV2.css";
import "./index.css";

export const StartDayV2 = () => {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const reducer = useDispatch();
  const isMobile = useGetDevice();
  const [checkDay, setCheckDay] = useState(false);
  const [isStartingDay, setIsStartingDay] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(0);
  const gridRef = useRef(null);
  const isMobilePos = useSelector(getMobilePos);
  const organizationId = localStorage.getItem("orgId");
  const locationId = localStorage.getItem("locId");
  const orgName = localStorage.getItem("orgName") || "Organization";
  const locName = localStorage.getItem("LocName") || "Location";
  const retailType = localStorage.getItem("retailType") || "";
  const branchSubType = localStorage.getItem("branchSubType") || "";

  const getTypeBadge = () => {
    if (retailType === "RESTAURANT") {
      if (branchSubType === "FINE_DINE")
        return { label: "Fine Dine", cls: "fine-dine", retailType };
      if (branchSubType === "QSR") return { label: "QSR", cls: "qsr", retailType };
      return { label: "Restaurant", cls: "restaurant", retailType };
    }
    if (retailType === "GROCERY") return { label: "Grocery", cls: "grocery", retailType };
    if (retailType === "RETAIL") return { label: "Retail OS", cls: "retail", retailType };
    return retailType ? { label: retailType, cls: "retail", retailType } : null;
  };
  const typeBadge = getTypeBadge();
  const { uidx, firstName, secondName, roles } = JSON.parse(
    localStorage.getItem("retailUserDetails") || "{}"
  );

  useEffect(() => {
    activeSessionDay(locationId).then((res) => {
      if (res?.data?.statusCode === 300) {
        setCheckDay(true);
      } else {
        const dayId = res?.data?.userSession?.sessionId;
        setInLocalStorage("daySessionId", dayId);
        setCheckDay(false);

        // Check whether this user already has an active session on a terminal.
        // If yes, restore it and skip the terminal selection page entirely.
        getActiveSession(uidx, locationId, dayId)
          .then((sessionRes) => {
            if (sessionRes?.data?.statusCode !== 300) {
              // Active session found — write it to localStorage and go home.
              localStorage.setItem("sessionId", sessionRes?.data?.userSession?.sessionId);
              localStorage.setItem("licenseId", sessionRes?.data?.userSession?.licenseId);
              setInLocalStorage("licenseName", sessionRes?.data?.userSession?.licenseName);
              if (sessionRes?.data?.hasOwnProperty("machines")) {
                localStorage.setItem(
                  "machineId",
                  sessionRes?.data?.machines?.[0]?.machineId || 0
                );
              }
              // Restaurant requires layoutId before entering the homepage.
              tableLayoutAvailability(locationId)
                .then((tRes) => {
                  localStorage.setItem("layoutId", tRes?.data?.object?.[0]?.layoutId);
                })
                .catch(() => {})
                .finally(() => navigate("/dashboard"));
            } else {
              // No active session for this user — show the terminal list normally.
              getLicenseList();
            }
          })
          .catch(() => getLicenseList());
      }
    });
  }, []);

  const {
    data: licenseList,
    refetch: getLicenseList,
    isLoading: isLoadingLicenseList,
  } = useQuery({
    enabled: false,
    queryKey: ["licenseList"],
    queryFn: async () => {
      const daySessionId = localStorage.getItem("daySessionId");
      const response = await getStoreActivity(locationId, organizationId, daySessionId, isMobile);
      if (response?.data?.code === "ECONNRESET") {
        throw new Error(response?.data?.message);
      }
      if (response?.data?.statusCode === 15018) {
        return [];
      }
      if (!!response?.data?.es) {
        throw new Error(response?.data?.message);
      }
      if (response?.data?.closeSessionAllowed === true) {
        localStorage.removeItem("sessionId");
        localStorage.removeItem("licenseId");
        localStorage.removeItem("storeMode");
        reducer(addLicense({ licenseId: "", licenseName: "" }));
        reducer(addadvance(""));
        reducer(addpin(""));
        reducer(addmanagerpin(""));
        reducer(addmanageruidx(""));
      }
      return (
        response?.data?.licenses?.map((item) => ({
          value: item?.licenseId,
          label: item?.licenseName,
          active: item?.active,
        })) || []
      );
    },
    onError: (err) => {
      showSnackbar(err?.message || err?.response?.data?.message, "error");
    },
  });

  const handleStartDay = () => {
    setIsStartingDay(true);
    const role = roles?.includes("POS_MANAGER") ? "POS_MANAGER" : "POS_USER";
    const createdBy = `${firstName} ${secondName}`;
    startDay({ createdBy, uidx, role, organizationId, locationId })
      .then((res) => {
        if (res?.data?.code === "ECONNRESET" || !!res?.data?.es) {
          throw new Error(res?.data?.message);
        }
        showSnackbar("Day started successfully", "success");
        localStorage.setItem("daySessionId", res?.data?.userSession?.userSessionId);
        setCheckDay(false);
        setIsStartingDay(false);
        getLicenseList();
      })
      .catch((err) => {
        showSnackbar(err?.message || err?.response?.data?.message, "error");
        setIsStartingDay(false);
      });
  };

  const handleLogout = () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    const payload = {
      metaData: {
        os: navigator.platform,
        latitude: "",
        browser: window.navigator.userAgent,
        city: "",
        country: "",
        postCode: "",
        longitude: "",
        device: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(
          navigator.userAgent
        )
          ? "This device is Mobile"
          : /Windows/i.test(navigator.userAgent)
          ? "This device is Desktop"
          : "This device is tablet",
      },
    };

    logoutSession(payload)
      .then((res) => {
        showSnackbar("Successfully Logged out", "success");
        isMobilePos && emit({ type: "localData", data: null });
        Object.keys(localStorage).forEach((key) => {
          if (key !== "printerConfig") {
            localStorage.removeItem(key);
          }
        });
        navigate("/");
        setIsLoggingOut(false);
      })
      .catch((err) => {
        showSnackbar(err.message || "Logout failed", "error");
        isMobilePos && emit({ type: "localData", data: null });
        Object.keys(localStorage).forEach((key) => {
          if (key !== "printerConfig") {
            localStorage.removeItem(key);
          }
        });
        setIsLoggingOut(false);
      });
  };

  const handleSelectTerminal = (license) => {
    if (license.active) return;
    customEventTrigger("pos_session_form");
    reducer(addLicense({ licenseId: license.value, licenseName: license.label }));
    localStorage.setItem("selectedLicenseId", license.value);
    localStorage.setItem("selectedLicenseName", license.label);
    navigate("/session-page/opening");
  };

  const getColumns = () => {
    if (!gridRef.current) return 1;
    const cards = Array.from(gridRef.current.children);
    if (!cards.length) return 1;
    const firstTop = Math.round(cards[0].getBoundingClientRect().top);
    return cards.filter((c) => Math.round(c.getBoundingClientRect().top) === firstTop).length || 1;
  };

  useEffect(() => {
    if (checkDay) return;
    const list = licenseList || [];
    const handleKeyDown = (e) => {
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Enter"].includes(e.key)) return;
      e.preventDefault();
      if (e.key === "Enter") {
        if (list[focusedIdx]) handleSelectTerminal(list[focusedIdx]);
        return;
      }
      const cols = getColumns();
      setFocusedIdx((prev) => {
        if (e.key === "ArrowLeft") return Math.max(prev - 1, 0);
        if (e.key === "ArrowRight") return Math.min(prev + 1, list.length - 1);
        if (e.key === "ArrowUp") return Math.max(prev - cols, 0);
        if (e.key === "ArrowDown") return Math.min(prev + cols, list.length - 1);
        return prev;
      });
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [licenseList, focusedIdx, checkDay]);

  useEffect(() => {
    if (!gridRef.current) return;
    const card = gridRef.current.children[focusedIdx];
    card?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [focusedIdx]);

  return (
    <>
      {isLoggingOut && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            cursor: "not-allowed",
          }}
        />
      )}
      <div className="sd-page" style={{ opacity: isLoggingOut ? 0.8 : 1 }}>
        {/* Header card */}
        <div className="sd-header-card">
          <header className="sd-header">
            <div className="sd-header-left">
              <button
                type="button"
                className="sd-back-btn"
                onClick={() => navigate("/products/access-mode")}
                data-testid="button-session-start-back"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="sd-back-icon"
                  aria-hidden="true"
                >
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
              </button>
              <img
                src={PalletLogo}
                alt="Pallet logo"
                className="sd-logo"
                data-testid="img-session-start-logo"
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div className="sd-step-badge">Step 3 of 4</div>
              <div
                onClick={handleLogout}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: isLoggingOut ? "not-allowed" : "pointer",
                  color: "#e53e3e",
                }}
                title="Logout"
              >
                {isLoggingOut ? (
                  <ArrowPathIcon className="spinner" style={{ width: 20, height: 20 }} />
                ) : (
                  <LogoutIcon sx={{ fontSize: "1.25rem" }} />
                )}
                <span style={{ fontSize: "0.875rem", fontWeight: 600 }}>Logout</span>
              </div>
            </div>
          </header>

        </div>{/* end sd-header-card */}

        <div className="sd-body">
            <div className="sd-content-wrapper">
              {checkDay ? (
                /* ── No active day ── */
                <div className="sd-section-no-day">
                  <div className="sd-section-header">
                    <div>
                      <p className="sd-section-tag">DAY STATUS</p>
                      <h2 className="sd-section-title">No active day found</h2>
                    </div>
                  </div>
                  <p className="sd-empty-desc">
                    There is no active business day. Start the day to continue to session setup.
                  </p>
                  <button
                    className="sd-start-day-btn"
                    onClick={handleStartDay}
                    disabled={isStartingDay}
                  >
                    {isStartingDay ? (
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : (
                      <>
                        Start Day <ArrowForwardIcon fontSize="small" />
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <>
                  {/* Breadcrumbs */}
                  <div className="sd-top-bar">
                    <h1 className="sd-title" data-testid="text-session-start-title">
                      Start session
                    </h1>
                    <div className="sd-breadcrumbs">
                      <div className="sd-crumb org" data-testid="text-session-start-organization">
                        {orgName}
                      </div>
                      <div className="sd-crumb loc" data-testid="text-session-start-location">
                        {locName}
                      </div>
                    </div>
                  </div>

                  {/* Terminals section */}
                  <div className="sd-section">
                    <div className="sd-section-header">
                      <h2 className="sd-section-title">Active terminals</h2>
                      <p className="sd-section-desc">
                        Choosing a terminal moves you straight into system health checks.
                      </p>
                    </div>

                    {isLoadingLicenseList ? (
                      <div className="sd-loading">
                        <CircularProgress size={28} sx={{ color: "#2A71FF" }} />
                      </div>
                    ) : (
                      <div className="sd-term-grid" ref={gridRef}>
                        {(licenseList || []).map((license, idx) => {
                          const isLive = license?.active;
                          return (
                            <button
                              type="button"
                              disabled={isLive}
                              key={license?.value}
                              className={`sd-term-card${isLive ? " live" : " available"}${focusedIdx === idx ? " sd-kb-focused" : ""}`}
                              onClick={() => { setFocusedIdx(idx); handleSelectTerminal(license); }}
                              data-testid={`card-session-start-terminal-${license?.value}`}
                            >
                              <div className="sd-term-top">
                                <div className="sd-term-icon">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="sd-term-monitor-icon"
                                    aria-hidden="true"
                                  >
                                    <rect width="20" height="14" x="2" y="3" rx="2"></rect>
                                    <line x1="8" x2="16" y1="21" y2="21"></line>
                                    <line x1="12" x2="12" y1="17" y2="21"></line>
                                  </svg>
                                </div>
                                <div className="sd-term-check-container">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="sd-term-check-icon"
                                    aria-hidden="true"
                                  >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path d="m9 12 2 2 4-4"></path>
                                  </svg>
                                </div>
                              </div>
                              <div className="sd-term-details">
                                <h3
                                  className="sd-term-name"
                                  data-testid={`text-session-start-terminal-name-${license?.value}`}
                                >
                                  {license?.label}
                                </h3>
                                <div className="sd-term-badge-wrapper">
                                  <span
                                    className={`sd-status-badge ${isLive ? "live" : "available"}`}
                                    data-testid={`status-session-start-terminal-${license?.value}`}
                                  >
                                    {isLive ? "Live" : "Available"}
                                  </span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                        {(!licenseList || licenseList?.length === 0) && (
                          <p className="sd-empty-desc">No terminals found for this location.</p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
        </div>
      </div>
    </>
  );
};
